import { useQueryClient, useMutation } from '@tanstack/react-query';
import { ErrorEventsEnum, errorLogToRemoteUtil } from '@keymono/utilities';

import { IApiError } from '../../../../types';

import { IRemoveOrgIntegrationData } from '../types';

import { ORGANIZATION_API } from '../organizationAPIClient';
import { QUERY_FETCH_ORG_INTEGRATIONS_KEY } from './useOrgIntegrationsQuery';

const MUT_REMOVE_ORG_INTEGRATION = 'remove-org-integration-mutation-key';

interface IUseRemoveOrgIntegrationOptions {
  id: string;
  orgId: string;
  onSuccessCallback?: (removedData: IRemoveOrgIntegrationData) => void;
  onErrorCallback?: (error: IApiError) => void;
}

/**
 * This mutation removes the selected integration from the org.
 */
export const useRemoveOrgIntegrationMutation = ({
  id,
  orgId,
  onSuccessCallback,
  onErrorCallback,
}: IUseRemoveOrgIntegrationOptions) => {
  const reactQueryClient = useQueryClient();
  const { mutate, isLoading, data, error } = useMutation({
    mutationKey: [MUT_REMOVE_ORG_INTEGRATION, id, orgId],
    retry: false,
    mutationFn: ORGANIZATION_API.removeOrganizationIntegration,
    onSuccess: (responseData) => {
      reactQueryClient.invalidateQueries([
        QUERY_FETCH_ORG_INTEGRATIONS_KEY,
        orgId,
      ]);
      onSuccessCallback?.(responseData);
    },
    onError: (err: IApiError) => {
      errorLogToRemoteUtil({
        error: err,
        errorCode: ErrorEventsEnum.ERROR_IN_API_CALL,
        errorTitle: 'Error in Removing the Organization Integration Attempt',
        message: err.message,
      });
      onErrorCallback?.(err);

      return err;
    },
  });

  return {
    error,
    isLoading,
    data,
    removeOrganizationIntegration: mutate,
  };
};
